import { MultiStepFormConfig, BaseFormData } from './components/types';
import { AbnLookupStep, BusinessDetailsStep, ContactDetailsStep, SingleSelectionStep, TextFieldStep, TrustDetailsStep, MultiInputStep } from './steps';
import { createFormOptions } from './steps/baseFormOptions';
import { createFormValidations } from './steps/baseFormValidations';
import { 
  singleSelectionStepInitialData,
  abnLookupStepInitialData, 
  businessDetailsStepInitialData,
  contactDetailsStepInitialData,
  trustDetailsStepInitialData,
  multiInputStepInitialData,
  combineStepData,
  textFieldStepInitialData
} from './steps/initialData';
import { getUtmParamsForDynamics } from '@/lib/utm-tracking';

// Form options and validations - centralized in config for easy management
export const formOptions = createFormOptions({
  businessSelectionOptions: [
    {
      value: "practitioner",
      title: "I’m a health or dental care practitioner",
      description: "I want to streamline my regular business payments and earn rewards."
    },
    {
      value: "supplier",
      title: "I’m a health or dental care supplier",
      description: "I’d like to explore offering Points4Pay to my customers to streamline my payment collections."
    },
    // {
    //   value: "other",
    //   title: "Other",
    //   description: "My business doesn’t fit into the options above."
    // }
  ]
});

export const formValidations = createFormValidations({
  businessDetailsStep: (data: BaseFormData) => {
    // Basic validation for business details
    const stateValid = data.countryOfRegistration === "Other" ? true : !!(data.state && data.state !== 'Select');
    return !!(data.countryOfRegistration && data.businessType && stateValid);
  },
  businessSelectionValidation: (data: BaseFormData) => {
    return !!data.businessSelection;
  },
  trustDetailsStep: (data: BaseFormData) => {
    // Validation for trust details - only required if business type is Trust
    if (data.businessType !== 'Trust') return true;
    
    // Basic required fields
    const hasBasicFields = !!(data.trusteeType && data.trusteeName);
    
    // ABN/ACN only required if trustee is Corporate Entity
    const needsAbn = data.trusteeType === 'Corporate Entity';
    const hasAbn = needsAbn ? !!data.trustAbnAcn : true;
    
    return hasBasicFields && hasAbn;
  },
  frequentFlyerValidation: () => {
    return true;
  },
  contactDetailsStep: (data: BaseFormData) => {
    // Validate all required contact fields
    const firstNameValid = !!(data.firstName && typeof data.firstName === 'string' && data.firstName.trim().length >= 2);
    const lastNameValid = !!(data.lastName && typeof data.lastName === 'string' && data.lastName.trim().length >= 2);
    const workEmailValid = !!(data.workEmail && typeof data.workEmail === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.workEmail));
    const phoneValid = !!(data.phone && typeof data.phone === 'string' && data.phone.trim().length >= 8);
    const termsAcceptedValid = !!data.termsAccepted;
    
    // Conditional validation for referredBy field
    const shouldRequireReferredBy = data.howDidYouHear === "Business referral (e.g. accountant, client, partner)" ||
                                   data.howDidYouHear === "Financial adviser or broker";
    
    const referredByValid = shouldRequireReferredBy 
      ? !!(data.referredBy && typeof data.referredBy === 'string' && data.referredBy.trim().length >= 1)
      : true; // Not required if condition not met
    

    const creditReportConsentValid = !!data.creditReportConsent;
    return firstNameValid && lastNameValid && workEmailValid && phoneValid && termsAcceptedValid && referredByValid && creditReportConsentValid;
  },
});

// Combine initial data from selected steps
const initialFormData = combineStepData(
  singleSelectionStepInitialData,
  abnLookupStepInitialData,
  businessDetailsStepInitialData,
  contactDetailsStepInitialData,
  trustDetailsStepInitialData,
  textFieldStepInitialData,
  multiInputStepInitialData
  // Add more step data here as you add more steps
);

export const points4PayEnquireFormConfig: MultiStepFormConfig<BaseFormData> = {
  formId: 'points4pay-enquire',
  formOptions: formOptions,
  steps: [
    {
      id: 'business-selection',
      title: 'What best describes you?',
      description: 'Let’s start by understanding your role. This helps us guide your enquiry and tailor the experience.',
      component: SingleSelectionStep,
      validation: formValidations.businessSelectionValidation,
      stepConfig: {
        fieldName: 'businessSelection',
        optionsKey: 'businessSelectionOptions',
        title: 'What best describes your business?',
        description: undefined,
      },
      // infoBoxText: "<strong>Not listed?</strong> You can still complete the form. We’ll help guide you to the right solution.",
    },
    {
      id: 'business-search',
      title: 'Tell us about your business.',
      description: 'Start typing your ABN, ACN, or business name. We\’ll fill in the rest.',
      validation: formValidations.abnLookupStep,
      component: AbnLookupStep,
    },
    {
      id: 'business-details',
      title: 'Confirm your business details.',
      description: 'These details help us understand your structure and business needs.',
      stepConfig: {
        showTradingName: true,
        showCountryOfRegistration: true,
        showState: true,
        showBusinessType: true,
      },
      validation: formValidations.businessDetailsStep,
      component: BusinessDetailsStep,
    },
    {
      id: 'trust-details',
      title: 'Tell us more about your trust.',
      description: 'We ask this because finance contracts are held by the trustee on behalf of the trust.',
      component: TrustDetailsStep,
      stepConfig: {
        showTrustType: false,
        showTrusteeType: true,
        showAbnAcn: true,
        showTrusteeName: true,
        showTrustDirectorsEmail: true,
      },
      validation: formValidations.trustDetailsStep,
      shouldShow: (data: BaseFormData) => data.businessType === 'Trust',
    },
    {
      id: 'business-description-textfields',
      title: 'Describe your business.',
      description: 'This helps our team of finance specialists better understand your business needs.',
      component: TextFieldStep,
      validation: formValidations.textFieldStep,
      isOptional: true,
    },
    {
      id: 'frequent-flyer',
      title: 'Want to earn Frequent Flyer points on your payments?',
      description: 'If you\'d like to earn points on eligible payments, enter your membership numbers below. Typically, these would be the same as your ABN number. You can leave these blank. We\'ll follow up during onboarding if needed.',
      component: MultiInputStep,
      shouldShow: (data: BaseFormData) => data.businessSelection === 'practitioner',
      stepConfig: {
        inputFields: [
          {
            fieldName: 'qantasBusinessRewards',
            label: 'Qantas Business Rewards number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true, 
            helperText: 'Not a member yet? Join <a href="https://www.qantas.com/au/en/business-rewards/partner/join.html?code=PFOCTET" target="_blank">Qantas Business Rewards</a>',
          },
          {
            fieldName: 'virginAustraliaBusinessFlyer',
            label: 'Virgin Australia Business Flyer number',
            placeholder: ' ',
            inputType: 'text',
            isOptional: true,
            helperText: 'Not a member yet? Join <a href="https://www.virginaustralia.com/au/en/fly-for-business/business-flyer/business-flyer-application/?promocode=OCTETMBRM" target="_blank">Virgin Australia Business Flyer</a>',
          }
        ]
      }
    },
    {
      id: 'contact-details',
      title: 'Your contact details.',
      description: '<strong>How can we reach you?</strong> We\’ll get in touch within 1 working day to guide you through the next steps.',
      component: ContactDetailsStep,
      validation: formValidations.contactDetailsStep,
      stepConfig: {
        showTitle: true,
        showFirstName: true,
        showLastName: true,
        showWorkEmail: true,
        showPhone: true,
        showHowDidYouHear: true,
        showReferredBy: true,
        showMessage: true,
        showTermsAccepted: true,
        showMarketingConsent: true,
        showCreditReportConsent: true,
      },
    },
  ],
  initialData: initialFormData,
  thankYouPage: {
    title: 'Thanks for your enquiry',
    subtitle: '<strong>We\’ve received your details.</strong> We will be in touch shortly to discuss your business needs and next steps.',
    firstName: '{{firstName}}',
    nextStepsTitle: 'Here\’s what\’s next for',
    businessName: '{{businessName}}',
    nextStepsItems: [
      {
        icon: 'schedule',
        text: 'Our team will contact you within <strong>1 working day</strong>',
      },
      {
        icon: 'mail',
        text: 'You\’ll receive an <strong>email or a phone call</strong> from our specialist team.'
      },
      {
        icon: 'how_to_reg',
        text: 'We\’ll walk you through the process and <strong>answer any questions</strong>.'
      }
    ],
    ctaButton: {
      text: 'View Homepage',
      href: '/'
    },
    additionalInfo: 'In the meantime, stay in-the-know with helpful insights and business guides.',
    socialButton: {
      text: 'Follow Octet on LinkedIn',
      href: 'https://www.linkedin.com/company/octet-finance'
    }
  },
  onSubmit: async (data: BaseFormData) => {
    try {
      // Get UTM parameters from sessionStorage
      const utmParams = typeof window !== 'undefined' ? getUtmParamsForDynamics() : {
        ot_utmsource: '',
        ot_utmmedium: '',
        ot_utmterm: '',
        ot_utmcampaign: '',
      };

      // Override UTM parameters for Points4Pay
      const points4PayUtmParams = {
        ...utmParams,
        // ot_utmsource: 'Points4Pay Landing Page',
        // ot_utmcampaign: 'Points4Pay',
      };

      // Map form data to Dynamics field format
      // Note: Industry is automatically set to 'Healthcare' for Points4Pay
      const dynamicsPayload = {
        ot_topic: 'Points4Pay Leads',
        pb_title: (data.title as string) || '',
        ot_firstname: (data.firstName as string) || '',
        ot_lastname: (data.lastName as string) || '',
        ot_phone: (data.phone as string) || '',
        ot_workemail: (data.workEmail as string) || '',
        // Business details
        ot_country: (data.countryOfRegistration as string) || '',
        pb_postcode: (data.postcode as string) || '',
        ot_state: (data.state as string) || '',
        ot_businesstype: (data.businessType as string) || '',
        ot_industry: 'Healthcare', // Fixed for Points4Pay
        ot_interestedservice: 'Points4Pay',
        ot_businessname: (data.businessName as string) || '',
        ot_abn: (data.selectedABN as string) || '',
        pb_tradingname: (data.tradingName as string) || '',
        pb_businessdescription: (data.businessDescription as string) || '',
        ot_yourenquiry: (data.message as string) || '',
        // Business selection (Points4Pay specific)
        pb_businessselection: (data.businessSelection as string) || '',
        // Trust details (if applicable)
        pb_trusttype: (data.trusteeType as string) || '',
        pb_trusteename: (data.trusteeName as string) || '',
        pb_trustabnacn: (data.trustAbnAcn as string) || '',
        pb_trustdirectorsemail: (data.trustDirectorsEmail as string) || '',
        // Frequent Flyer details (only for practitioners)
        pb_qantasbusinessrewards: (data.qantasBusinessRewards as string) || '',
        pb_virginaustraliabusinessflyer: (data.virginAustraliaBusinessFlyer as string) || '',
        // Marketing consent
        ot_howdidyouhearaboutus: (data.howDidYouHear as string) || '',
        pb_referredby: (data.referredBy as string) || '',
        ot_subscribedtonewsletter: (data.marketingConsent ? 'true' : 'false'),
        ot_acceptedtermsconditions: (data.termsAccepted ? 'true' : 'false'),
        pb_creditreportconsent: (data.creditReportConsent ? 'true' : 'false'),
        // UTM parameters
        ...points4PayUtmParams,
        // Include reCAPTCHA token (already added by MultiStepForm)
        recaptchaToken: (data.recaptchaToken as string) || '',
      };
      
      // Submit to Dynamics via API route
      const response = await fetch('/api/dynamics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dynamicsPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Failed to submit form');
      }

      console.info('Form submitted successfully:', result);
    } catch (error) {
      console.error('Form submission error:', error);
      throw error; // Re-throw to let MultiStepForm handle the error display
    }
  },
  showProgress: true,
  allowBackNavigation: true,
  requireRecaptcha: true,
  onStepChange: (currentStep, totalSteps) => {
    console.info(`Points4Pay Form - Step ${currentStep} of ${totalSteps}`);
  },
};
